import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardDocumentIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { userAppContext } from '@/context/Context';
import { useUserApiKey } from '@/hooks/useUserApiKey';
import { useAuthStatus } from '@/store';
import Alert from '@/global/Alert/Alert';
import { isNativeApiConsoleEnabled } from '@/components/API/Console/isNativeApiConsoleEnabled';

const STATUS_MESSAGES: Record<string, string> = {
  standard: 'API keys are not available on the Standard plan.',
  external: 'API key management is handled by your identity provider.',
  unset: 'Loading your API key…',
};

type ApiKeyQuickAccessProps = {
  embedded?: boolean;
};

export default function ApiKeyQuickAccess({ embedded = false }: ApiKeyQuickAccessProps) {
  const { isAuthenticated, isAuthenticating } = useAuthStatus();
  const { user, userApiKey: contextUserApiKey, setUserApiKey: contextSetUserApiKey } = userAppContext();
  const { userApiKey, apiKeyDisplayStatus } = useUserApiKey({
    user,
    userApiKey: contextUserApiKey,
    setUserApiKey: contextSetUserApiKey,
  });

  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!userApiKey) return;
    navigator.clipboard.writeText(userApiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
  }, [userApiKey]);

  if (isAuthenticating) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <div className={embedded ? 'text-sm' : 'mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4'}>
        <p className={embedded ? 'mb-2 text-amber-900' : 'mb-2 text-sm text-amber-900'}>
          Sign in to view your API key and authorize requests in the console below.
        </p>
        <Link to="/login" className="text-sm font-semibold text-blue-600 underline">
          Sign in
        </Link>
      </div>
    );
  }

  const inputValue = userApiKey ?? STATUS_MESSAGES[apiKeyDisplayStatus] ?? apiKeyDisplayStatus;

  return (
    <>
      <div className={embedded ? undefined : 'mb-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4'}>
        {!embedded && (
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Your API key</h2>
            <Link to="/Profile-area" className="text-xs font-medium text-blue-600 underline">
              Manage in profile
            </Link>
          </div>
        )}
        {embedded && (
          <div className="mb-2 flex justify-end">
            <Link to="/Profile-area" className="text-xs font-medium text-blue-600 underline">
              Manage in profile
            </Link>
          </div>
        )}
        <p className={`mb-3 text-gray-600 ${embedded ? 'text-xs' : 'text-sm'}`}>
          {isNativeApiConsoleEnabled() ? (
            embedded ? (
              <>Copy your key, then paste it in the API Console header to authorize requests.</>
            ) : (
              <>
                Copy your key, then paste it in the Interactive Console header (or open the console and use the
                API key field) before sending requests.
              </>
            )
          ) : embedded ? (
            <>
              Copy your key, then in Swagger click <strong>Authorize</strong> and paste it there.
            </>
          ) : (
            <>
              Copy your key, then in the Swagger UI below click <strong>Authorize</strong> (top right, next to
              the padlock) and paste it into the authorization dialog.
            </>
          )}
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            type={isApiKeyVisible && userApiKey ? 'text' : 'password'}
            className="min-w-0 flex-1 rounded-xl border bg-white p-2 text-sm font-mono"
            value={inputValue}
            aria-label="Your API key"
          />
          <button
            type="button"
            className="rounded-xl border bg-white px-3 py-2"
            onClick={() => setIsApiKeyVisible(prev => !prev)}
            disabled={!userApiKey}
            aria-label={isApiKeyVisible ? 'Hide API key' : 'Show API key'}
          >
            {isApiKeyVisible ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            className="rounded-xl border bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleCopy}
            disabled={!userApiKey}
            aria-label="Copy API key"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {copied && <Alert message="API key copied to clipboard" type="success" />}
    </>
  );
}
